import { A11yModule } from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	ElementRef,
	forwardRef,
	inject,
	OnDestroy,
	OnInit,
	QueryList,
	signal,
	ViewChild,
	ViewChildren
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
	catchError,
	finalize,
	forkJoin,
	map,
	Observable,
	of,
	Subject,
	switchMap,
	take,
	takeUntil
} from 'rxjs';
import { ActivityTaskPaneComponent } from '@go-modules/components/activity-task-pane/activity-task-pane.component';
import {
	TaskListNavigatorComponent
} from '@go-modules/components/activity-task-sessions/task-list-navigator/task-list-navigator.component';
import { ConfirmDialogComponent } from '@go-modules/components/dialogs/confirm-dialog/confirm-dialog.component';
import { DisabledInteractiveDirective } from '@go-modules/directives/disabled-interactive/disabled-interactive.directive';
import { GoDialogContentDirective } from '@go-modules/directives/go-dialog-content/go-dialog-content.directive';
import { GoToastStatusType } from '@go-modules/enums/go-toast-status-type';
import { Activity } from '@go-modules/interfaces/activity';
import type { OrgSettings } from '@go-modules/interfaces/groups/org-settings';
import { Task, TaskType } from '@go-modules/interfaces/task';
import { NgxActivityService } from '@go-modules/services/activity/activity.service';
import { ENVIRONMENTS, LTI_ENVIRONMENT_MODES } from '@go-modules/services/environment-vars/environments';
import { GoDialogRef } from '@go-modules/services/go-dialog-ref/go-dialog-ref';
import { GO_MODAL_DATA } from '@go-modules/services/go-modal/go-modal.tokens';
import { NgxGoToastService } from '@go-modules/services/go-toast/go-toast.service';
import { NgxTaskService } from '@go-modules/services/task/task.service';
import { ResponsiveLayoutService } from '@go-modules/services/responsive-layout/responsive-layout.service';
import { ACTIVITY_EDITOR_CONTEXT } from './activity-editor-context';
import type {
	ActivityEditorContext,
	AdditionalSettingsValidator
} from './activity-editor-context';
import { ActivityPreviewEditorStore, activityEquivalent, tasksEquivalent } from './activity-preview-editor-store';
import {
	buildActivityPreviewEditorSavePayload,
	type ActivityPreviewEditorDialogData,
	type ActivityPreviewEditorDialogResult,
	getActivityPreviewEditorSaveErrorMessage,
	getActivityPreviewEditorDialogDataError,
	getActivityPreviewEditorInitialPreviewPanel,
	syncSelectedActivityAfterSave
} from './activity-preview-editor-dialog.shared';
import { ActivityDetailsComponent } from './activity-details/activity-details.component';
import {
	ActivityPreviewTaskPanelComponent
} from './activity-preview-task-panel/activity-preview-task-panel.component';
import { GoModalService } from '@go-modules/services/go-modal/go-modal.service';
import { ActivityPreviewDialogComponent } from '../activity-preview/activity-preview-dialog.component';
import {
	ActivityPreviewInlineTaskInstructionStateService
} from './activity-preview-inline-task-instruction-state.service';

@Component({
	selector: 'activity-preview-editor-dialog',
	standalone: true,
	imports: [
		CommonModule,
		TranslateModule,
		A11yModule,
		ActivityTaskPaneComponent,
		ActivityDetailsComponent,
		ActivityPreviewTaskPanelComponent,
		DisabledInteractiveDirective,
		GoDialogContentDirective,
		MatTooltipModule,
		TaskListNavigatorComponent
	],
	providers: [
		ActivityPreviewEditorStore,
		ActivityPreviewInlineTaskInstructionStateService,
		{
			provide: ACTIVITY_EDITOR_CONTEXT,
			useExisting: forwardRef(() => ActivityPreviewEditorDialogComponent)
		}
	],
	host: {
		'(keydown)': 'onKeyDown($event)'
	},
	templateUrl: './activity-preview-editor-dialog.component.html',
	styleUrls: ['./activity-preview-editor-dialog.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ActivityPreviewEditorDialogComponent implements ActivityEditorContext, OnInit, OnDestroy {
	private readonly contextOrgSettings = signal<Partial<OrgSettings> | null>(null);
	private readonly additionalSettingsValidators: AdditionalSettingsValidator[] = [];
	private readonly destroy$ = new Subject<void>();
	private readonly inlineTaskInstructionState = inject(ActivityPreviewInlineTaskInstructionStateService);
	private statusMessageTimeout: ReturnType<typeof setTimeout> | null = null;
	private focusTimeouts: ReturnType<typeof setTimeout>[] = [];
	@ViewChild('taskOverviewTitle') private taskOverviewTitle?: ElementRef<HTMLElement>;
	@ViewChild('taskPreviewPanel') private taskPreviewPanel?: ElementRef<HTMLElement>;
	@ViewChildren('previewTab') private previewTabs: QueryList<ElementRef<HTMLButtonElement>>;

	public readonly store = inject(ActivityPreviewEditorStore);
	public readonly data = inject(GO_MODAL_DATA) as ActivityPreviewEditorDialogData;
	public readonly dialogTitleId = 'activity-preview-editor-dialog-title';
	public readonly isSaveRequired = this.data.isSaveRequired === true;
	public readonly canNavigateBack = this.isSaveRequired && typeof this.data.onNavigateBack === 'function';
	public readonly activity = this.store.activity.asReadonly();
	public readonly tasks = this.store.tasks.asReadonly();
	public readonly selectedTaskType = this.store.selectedTaskType.asReadonly();
	public readonly selectedTask = this.store.selectedTask;
	public readonly group = computed(() => this.data.group ?? null);
	public readonly account = computed(() => this.data.account ?? null);
	public readonly org = computed(() => this.data.org ?? null);
	public readonly license = computed(() => this.data.license ?? null);
	public readonly environment = computed(() => this.data.environment ?? null);
	public readonly isEditMode = signal(true);
	public readonly isSaving = this.store.isSaving.asReadonly();
	public readonly isDirty = this.store.isDirty;
	public readonly orgSettings = this.contextOrgSettings.asReadonly();
	public readonly statusMessage = signal('');
	public readonly activePreview = signal<'details' | 'task'>('details');
	public readonly isPreviewUnavailable = computed(() => this.activity() == null || this.isSaving());
	public readonly canSave = this.store.canSave;
	public readonly isTaskOverviewActive = computed(() =>
		this.activePreview() === 'task' && this.selectedTaskType() == null
	);
	public readonly taskOverviewPagerTotal = computed(() =>
		this.store.previewTasks().length + 1
	);
	public readonly shouldShowTaskPane = computed(() =>
		!this.isMobilePreview() || this.isTaskOverviewActive()
	);
	public readonly showMasterActivityToggle = computed(() =>
		this.data.libraryContext != null &&
		this.data.libraryContext.isOrgCollectionAndCanModifyItems === true
	);
	public readonly isActivityNameReadonly = computed(() =>
		this.environment()?.name === ENVIRONMENTS.LTI &&
		this.environment()?.mode !== LTI_ENVIRONMENT_MODES.COURSE_LEVEL
	);

	private readonly dialogRef = inject(GoDialogRef);
	private readonly activityService = inject(NgxActivityService);
	private readonly taskService = inject(NgxTaskService);
	private readonly matDialog = inject(MatDialog);
	private readonly translate = inject(TranslateService);
	private readonly toastService = inject(NgxGoToastService);
	private readonly responsiveLayoutService = inject(ResponsiveLayoutService);
	private readonly modal = inject(GoModalService);
	private readonly isMobilePreview = signal(false);
	public readonly title = computed(() => this.resolveTitle());
	public readonly activityTypeLabel = computed(() => this.resolveActivityTypeLabel());

	public ngOnInit (): void {
		this.initializeResponsiveState();
		this.contextOrgSettings.set(this.resolveOrgSettings());
		this.initializeStore();
	}

	public ngOnDestroy (): void {
		this.destroy$.next();
		this.destroy$.complete();
		this.clearStatusMessageTimeout();
		this.clearFocusTimeouts();
	}

	public onKeyDown (event: KeyboardEvent): void {
		if (event.key !== 'Escape') {
			return;
		}

		event.preventDefault();
		event.stopPropagation();
		if (this.isSaveRequired) {
			return;
		}

		this.requestCancel();
	}

	private resolveTitle (): string {
		const activity = this.activity();
		if (activity?.name) {
			return activity.name;
		}

		return this.data.mode === 'create'
			? this.translate.instant('activity-editor-panel-header_create')
			: this.translate.instant('activity-editor-panel-header_edit');
	}

	private resolveActivityTypeLabel (): string {
		const activity = this.activity();
		if (activity == null) {
			return '';
		}

		if (activity.is_conversation && activity.has_response_media) {
			return activity.live_session_enabled
				? this.translate.instant('common_standard-group-with-live-review')
				: this.translate.instant('common_standard-group');
		}

		if (activity.live_session_enabled) {
			return activity.template?.slug === 'stimulus_response'
				? this.translate.instant('common_stimulus-with-live-review')
				: this.translate.instant('common_standard-with-live-review');
		}

		return activity.template?.type ?? '';
	}

	public selectTask (type: TaskType): void {
		this.store.selectTask(type);
		if (this.store.selectedTaskType() === type) {
			this.activePreview.set('task');
		}
	}

	public selectActivityDetails (): void {
		this.store.resetSelectedTaskType();
		this.activePreview.set('details');
	}

	public selectTaskList (): void {
		this.store.resetSelectedTaskType();
		this.activePreview.set('task');
	}

	public openTaskList (): void {
		this.selectTaskList();
		this.queueFocusCallback(() => this.taskOverviewTitle?.nativeElement.focus());
	}

	public openFirstTaskFromOverview (): void {
		const taskType = this.store.previewTasks()[0]?.type ?? null;
		if (taskType == null) {
			return;
		}

		this.selectTask(taskType);
		this.queueFocusCallback(() => this.focusTaskDetailTitle());
	}

	public onTaskPaneTaskSelected (task: Task | void): void {
		if (task == null) {
			if (this.isTaskOverviewActive()) {
				this.queueFocusCallback(() => this.taskOverviewTitle?.nativeElement.focus());
			}
			return;
		}

		this.queueFocusCallback(() => this.focusTaskDetailTitle());
	}

	public activatePreviewTab (tab: 'details' | 'task'): void {
		if (tab === 'details') {
			this.selectActivityDetails();
			return;
		}

		this.selectTaskList();
	}

	public onPreviewTabKeydown (event: KeyboardEvent, currentTab: 'details' | 'task'): void {
		let targetTab: 'details' | 'task' | null = null;

		switch (event.key) {
			case 'ArrowLeft':
			case 'ArrowRight':
				targetTab = currentTab === 'details' ? 'task' : 'details';
				break;
			case 'Home':
				targetTab = 'details';
				break;
			case 'End':
				targetTab = 'task';
				break;
			case 'Enter':
			case ' ':
				targetTab = currentTab;
				break;
		}

		if (targetTab == null) {
			return;
		}

		event.preventDefault();
		event.stopPropagation();
		this.activatePreviewTab(targetTab);
		this.queueFocusCallback(() => this.focusPreviewTab(targetTab));
	}

	public updateActivity (activity: Activity): void {
		this.store.updateActivity(activity);
	}

	public updateTask (task: Task): void {
		this.store.updateTask(task);
		this.inlineTaskInstructionState.syncActiveTasks(this.store.tasks());
	}

	public updateTasks (tasks: Task[]): void {
		this.store.updateTasks(tasks);
		this.inlineTaskInstructionState.syncActiveTasks(this.store.tasks());
	}

	public registerAdditionalSettingsValidator (validator: AdditionalSettingsValidator): void {
		const existingIndex = this.additionalSettingsValidators.findIndex((item) =>
			validator.taskType != null && item.taskType === validator.taskType
		);

		if (existingIndex >= 0) {
			this.additionalSettingsValidators[existingIndex] = validator;
			return;
		}

		this.additionalSettingsValidators.push(validator);
	}

	public validateAdditionalSettings (): boolean {
		const invalidValidator = this.additionalSettingsValidators.find((validator) =>
			!validator.validateAdditionalSettingsForm()
		);

		if (invalidValidator == null) {
			this.clearStatusMessageTimeout();
			this.clearFocusTimeouts();
			this.statusMessage.set('');
			this.store.setValidationTarget(null);
			return true;
		}

		this.announceStatusMessage(
			this.translate.instant('activity-preview-editor-validation-error')
		);
		if (invalidValidator.taskType != null) {
			this.activePreview.set('task');
			this.store.setValidationTarget(invalidValidator.taskType);
		} else {
			this.selectActivityDetails();
			this.store.setValidationTarget(null);
		}
		this.queueFocusCallback(() => invalidValidator.focusFirstInvalidField?.());
		return false;
	}

	public requestSaveAndClose (): void {
		if (!this.canSave()) {
			return;
		}

		this.saveAndClose();
	}

	public requestPreview (): void {
		if (this.isPreviewUnavailable()) {
			return;
		}

		this.modal.open(ActivityPreviewDialogComponent, false, {
			data: {
				activity: this.activity(),
				tasks: this.store.previewTasks()
			}
		});
	}

	public requestCancel (): void {
		if (this.isSaveRequired) {
			if (this.canNavigateBack) {
				this.requestNavigateBack();
			}
			return;
		}

		if (this.isSaving()) {
			return;
		}

		if (!this.isDirty()) {
			this.closeDismissed();
			return;
		}

		this.matDialog.open(ConfirmDialogComponent, {
			data: {
				title: this.translate.instant('activity-settings_controller-unsaved-changes'),
				message: this.translate.instant('activity-settings_controller-message-2'),
				confirmText: this.translate.instant('common_discard-changes'),
				cancelText: this.translate.instant('common_cancel')
			}
		}).afterClosed()
			.pipe(take(1), takeUntil(this.destroy$))
			.subscribe((confirmed: boolean) => {
				if (confirmed) {
					this.closeDismissed();
				}
			});
	}

	public requestNavigateBack (): void {
		if (!this.canNavigateBack || this.isSaving()) {
			return;
		}

		this.dialogRef.close({dismissed: true});
		this.data.onNavigateBack?.();
	}

	private initializeStore (): void {
		this.store.isLoading.set(true);
		this.statusMessage.set(this.translate.instant('common_loading'));
		this.resolveInitialActivityAndTasks()
			.pipe(
				take(1),
				takeUntil(this.destroy$),
				finalize(() => {
					this.store.isLoading.set(false);
				})
			)
			.subscribe({
				next: ({activity, tasks}) => {
					if (this.data.mode === 'create') {
						this.store.initializeForCreate(activity, tasks, this.data.initialTaskType ?? null);
					} else {
						this.store.initializeForEdit(activity, tasks, this.data.initialTaskType ?? null);
					}
					this.activePreview.set(getActivityPreviewEditorInitialPreviewPanel(this.selectedTaskType()));
					this.statusMessage.set('');
				},
				error: (error) => {
					this.store.error.set(error);
					this.statusMessage.set(this.translate.instant('common_unknown-error_message'));
				}
			});
	}

	private initializeResponsiveState (): void {
		this.responsiveLayoutService.isMobileSize$
			.pipe(takeUntil(this.destroy$))
			.subscribe((isMobile) => {
				this.isMobilePreview.set(isMobile);
				if (!isMobile && this.isTaskOverviewActive()) {
					this.selectActivityDetails();
				}
			});
	}

	private resolveInitialActivityAndTasks (): Observable<{activity: Activity; tasks: Task[]}> {
		const dialogDataError = getActivityPreviewEditorDialogDataError(this.data);
		if (dialogDataError != null) {
			return this.missingDialogData(dialogDataError);
		}

		if (this.data.mode === 'create') {
			if (this.data.activityDraft == null) {
				return this.missingDialogData('Create activity preview editor dialogs require an activityDraft.');
			}

			const activityDraft = this.createDefaultActivityDraft(this.data.activityDraft);
			if (activityDraft.parent_id != null) {
				return this.resolveTasksForActivityId(activityDraft.parent_id)
					.pipe(map((tasks) => ({activity: activityDraft, tasks})));
			}

			const tasks = this.resolveCreateTasks(activityDraft);
			return of({activity: activityDraft, tasks});
		}

		if (this.data.activityDraft != null) {
			const activityDraft = this.data.activityDraft;
			return this.resolveTasksForActivity(activityDraft)
				.pipe(map((tasks) => ({activity: activityDraft, tasks})));
		}

		if (this.data.activityId != null) {
			return forkJoin({
				activity: this.activityService.get(this.data.activityId),
				tasks: this.resolveTasksForActivityId(this.data.activityId)
			}).pipe(
				map(({activity, tasks}) => ({
					activity,
					tasks: this.resolveDefaultTasksWhenEmpty(activity, tasks)
				}))
			);
		}

		return this.missingDialogData('Edit activity preview editor dialogs require an activityId or activityDraft.');
	}

	private createDefaultActivityDraft (activityDraft: Activity): Activity {
		return {
			...activityDraft,
			live_session_enabled: this.data.tasks == null
				? false
				: activityDraft.live_session_enabled ?? false
		};
	}

	private resolveCreateTasks (activityDraft: Activity): Task[] {
		if (this.data.tasks != null) {
			return this.data.tasks;
		}

		return this.taskService.getDefaultTasks(activityDraft)
			.filter((task) => task.type !== TaskType.React || activityDraft.peer_review === true);
	}

	private resolveTasksForActivityId (activityId: number): Observable<Task[]> {
		if (this.data.tasks != null) {
			return of(this.data.tasks);
		}

		return this.taskService.getTasks(activityId).pipe(take(1));
	}

	private resolveTasksForActivity (activity: Activity): Observable<Task[]> {
		if (this.data.tasks != null) {
			return of(this.data.tasks);
		}

		if (activity.activity_id != null) {
			return this.taskService.getTasks(activity.activity_id).pipe(
				take(1),
				map((tasks) => this.resolveDefaultTasksWhenEmpty(activity, tasks))
			);
		}

		return of(this.taskService.getDefaultTasks(activity));
	}

	private resolveDefaultTasksWhenEmpty (activity: Activity, tasks: Task[]): Task[] {
		if (this.data.tasks != null || tasks.length > 0) {
			return tasks;
		}

		return this.resolveCreateTasks(activity);
	}

	private saveAndClose (): void {
		if (this.store.isSaving()) {
			return;
		}

		if (!this.validateAdditionalSettings()) {
			return;
		}

		const activity = this.activity();
		if (activity == null) {
			return;
		}

		const saveTasks = this.getSaveTasks(activity);
		if (this.data.mode === 'edit' && !this.isDirty()) {
			this.closeWithResult(activity, saveTasks);
			return;
		}

		if (this.changesRequireWarning()) {
			this.matDialog.open(ConfirmDialogComponent, {
				data: {
					title: this.translate.instant('modal-activity-edit-warning_edit-activity'),
					message: this.translate.instant('modal-activity-edit-warning_warning-text', {
						total: activity.usage
					}),
					confirmText: this.translate.instant('common_continue')
				}
			}).afterClosed()
				.pipe(take(1), takeUntil(this.destroy$))
				.subscribe((confirmed: boolean) => {
					if (confirmed) {
						this.performSave(activity, saveTasks);
					}
				});
			return;
		}

		this.performSave(activity, saveTasks);
	}

	private performSave (activity: Activity, saveTasks: Task[]): void {
		this.store.isSaving.set(true);
		this.statusMessage.set(this.translate.instant('feedback-session_controller-saving'));

		const payload = buildActivityPreviewEditorSavePayload(this.data, activity, saveTasks);
		const save$ = this.data.mode === 'create'
			? this.activityService.save(payload)
			: this.activityService.update(payload);

		save$.pipe(
			switchMap((savedActivity) => this.resolveSavedTasks(savedActivity, saveTasks)),
			takeUntil(this.destroy$),
			catchError((error) => {
				const message = getActivityPreviewEditorSaveErrorMessage(error);
				this.statusMessage.set(message);
				this.toastService.createToast({
					type: GoToastStatusType.ERROR,
					message,
					data: {error}
				});
				return of(null);
			}),
			finalize(() => {
				this.store.isSaving.set(false);
			})
		).subscribe((result) => {
			if (result == null) {
				return;
			}

			this.store.markSaved(result.activity, result.tasks);
			this.taskService.updateTasks(result.activity.activity_id, result.tasks);
			syncSelectedActivityAfterSave(this.data.syncSelectedActivity, result.activity);
			this.toastService.createToast({
				type: GoToastStatusType.SUCCESS,
				message: this.data.mode === 'create'
					? 'folder-view_activity_added'
					: 'folder-view_activity_updated'
			});
			this.closeWithResult(result.activity, result.tasks);
		});
	}

	private resolveSavedTasks (
		savedActivity: Activity & {tasks?: Task[]},
		fallbackTasks: Task[]
	): Observable<ActivityPreviewEditorDialogResult> {
		if (savedActivity.tasks != null) {
			return of({
				activity: savedActivity,
				tasks: this.mergeSavedTaskInstructions(savedActivity.tasks, fallbackTasks),
				mode: this.data.mode
			});
		}

		if (this.data.mode === 'create') {
			return this.taskService.getTasks(savedActivity.activity_id).pipe(
				take(1),
				map((tasks) => ({
					activity: savedActivity,
					tasks,
					mode: this.data.mode
				}))
			);
		}

		return of({
			activity: savedActivity,
			tasks: fallbackTasks,
			mode: this.data.mode
		});
	}

	private getSaveTasks (activity: Activity): Task[] {
		return this.store.previewTasks()
			.map((task) => this.withInstructionState(task))
			.map((task) => this.removeModifier(activity, task));
	}

	private withInstructionState (task: Task): Task {
		return {
			...task,
			instruction: task.instruction ?? null
		};
	}

	private mergeSavedTaskInstructions (savedTasks: Task[], fallbackTasks: Task[]): Task[] {
		const fallbackTasksByType = new Map(fallbackTasks.map((task) => [task.type, task]));

		return savedTasks.map((task) => {
			const fallbackTask = fallbackTasksByType.get(task.type);

			return {
				...task,
				instruction: task.instruction === undefined
					? fallbackTask?.instruction ?? null
					: task.instruction
			};
		});
	}

	private removeModifier (activity: Activity, task: Task): Task {
		if (task.name) {
			const defaultTitleAndDescription = this.activityService.getTaskTitleAndDescription(
				activity,
				{ ...task, name: null } as Task
			);

			if (task.name === defaultTitleAndDescription.title) {
				return { ...task, name: null };
			}
		}

		return task;
	}

	private changesRequireWarning (): boolean {
		const activity = this.activity();
		const originalActivity = this.store.originalActivity();
		if (this.data.mode !== 'edit' || activity == null || originalActivity == null || !activity.usage) {
			return false;
		}

		return !activityEquivalent(
			this.withoutSafeWarningFields(activity),
			this.withoutSafeWarningFields(originalActivity)
		) || !tasksEquivalent(this.tasks(), this.store.originalTasks());
	}

	private withoutSafeWarningFields (activity: Activity): Activity {
		const {name: _name, ...remainingActivity} = activity;
		return remainingActivity as Activity;
	}

	private closeWithResult (activity: Activity, tasks: Task[]): void {
		this.dialogRef.close({
			activity,
			tasks,
			mode: this.data.mode
		} as ActivityPreviewEditorDialogResult);
	}

	private closeDismissed (): void {
		this.dialogRef.close({dismissed: true});
	}

	private missingDialogData (message: string): Observable<never> {
		return new Observable((observer) => {
			observer.error(new Error(message));
		});
	}

	private resolveOrgSettings (): Partial<OrgSettings> | null {
		if (this.data.libraryContext?.orgSettings != null) {
			return this.data.libraryContext.orgSettings;
		}

		const org = this.data.org as { org_settings?: Partial<OrgSettings> } | null;
		return org?.org_settings ?? null;
	}

	private announceStatusMessage (message: string): void {
		this.clearStatusMessageTimeout();

		this.statusMessage.set('');
		this.statusMessageTimeout = setTimeout(() => {
			this.statusMessage.set(message);
			this.statusMessageTimeout = null;
		});
	}

	private clearStatusMessageTimeout (): void {
		if (this.statusMessageTimeout == null) {
			return;
		}

		clearTimeout(this.statusMessageTimeout);
		this.statusMessageTimeout = null;
	}

	public queueFocusCallback (callback: () => void): void {
		this.clearFocusTimeouts();

		const timeout = setTimeout(() => {
			this.focusTimeouts = this.focusTimeouts.filter((item) => item !== timeout);
			callback();
		});

		this.focusTimeouts.push(timeout);
	}

	private clearFocusTimeouts (): void {
		if (!this.focusTimeouts.length) {
			return;
		}

		this.focusTimeouts.forEach((timeout) => clearTimeout(timeout));
		this.focusTimeouts = [];
	}

	private focusPreviewTab (tab: 'details' | 'task'): void {
		const tabIndex = tab === 'details' ? 0 : 1;
		this.previewTabs?.get(tabIndex)?.nativeElement.focus();
	}

	private focusTaskDetailTitle (): void {
		this.taskPreviewPanel?.nativeElement
			.querySelector<HTMLElement>('.task-details-title')
			?.focus();
	}
}
